import * as R from 'ramda';
import { createSelector } from 'reselect';

import {
  getProductsInShoppingCart,
  getVisibleTotalDiscount,
  getTotalTax,
  getTotalNet,
  getTotal,
  getShoppingCartDiscountsDict,
} from 'reducers/ShoppingCart';
import { UniquePrefixWordlist } from 'utils/diceware';
import { getPluginSelector, getPluginConfiguration } from 'reducers/Plugins';
import { getClientCode, getCompany } from 'reducers/Login';
import { getSelectedPos } from 'reducers/PointsOfSale';
import {
  getCurrentPosLanguage,
  getSetting,
  getCurrencySymbol,
  getShowPricesWithTax,
  getCurrencyFormatter,
} from 'reducers/configs/settings';
import { getSelectedWarehouse } from 'reducers/warehouses';
import { CartItem } from 'containers/Forms/GiftCardSerial/types';
import {
  OPEN_MODAL_PAGE,
  CLOSE_MODAL_PAGE,
  modalPages as mp,
} from 'constants/modalPage';

import { Configuration, ProcessingCustomerState, QrCodeConfig } from '../types';
import defaultTranslations from '../constants/defaultTranslations';
import {
  defaultCustomerCreationFieldsDisplay,
  pluginID,
  defaultQrCodeConfig,
} from '../constants';
import { ACTIONS } from '../constants/actions';

import {
  SET_SCREENS,
  SET_FETCHED_INITIAL_SCREEN_DETAILS,
  SET_SELECTED_SCREEN,
  SET_HAS_PERMISSION,
  SET_NEEDS_START_NEW,
  SET_PROCESSING_CUSTOMER,
  SET_NEED_TO_CLOSE_CUSTOMER_FORM_ON_DISPLAY,
  RESET_CUSTOMER_FORM_STATE,
} from './actionTypes';

interface ScreenDetailed extends Screen {
  availLeft: number;
  availTop: number;
  left: number;
  top: number;
  isPrimary: boolean;
  isInternal: boolean;
  devicePixelRatio: number;
  label: string;
}

type CustomerDisplayState = {
  hasPermission: boolean | null;
  fetchedInitialScreenDetails: boolean;
  screens: ScreenDetailed[];
  selectedScreen: { screen: ScreenDetailed; index: number } | null;
  needsStartNew: boolean;
  // Used to track what kind of messages should be sent on closing customer forms
  processingCustomerState: ProcessingCustomerState;
  needToCloseCustomerFormOnDisplay: boolean;
};

const initState: CustomerDisplayState = {
  hasPermission: null,
  fetchedInitialScreenDetails: false,
  screens: [],
  selectedScreen: null,
  needsStartNew: false,
  processingCustomerState: null,
  needToCloseCustomerFormOnDisplay: false,
};

const customerDisplayReducer = (
  state = initState,
  action,
): CustomerDisplayState => {
  switch (action.type) {
    // Closing edit customer form opens the customerView modal instead of closing the edit customer one
    case OPEN_MODAL_PAGE: {
      const hasClosedEditCustomer: boolean =
        // When closing the edit customer, we open customerView with a customer id and `replace: true` flag
        action.component === mp.customerView && action.props.customerId;
      const updateCustomerFormOpenedOnDisplay =
        state.processingCustomerState === 'update';

      if (hasClosedEditCustomer && updateCustomerFormOpenedOnDisplay) {
        return { ...state, needToCloseCustomerFormOnDisplay: true };
      }
      return state;
    }
    // Closing customer registration form closes it, doesn't re-open anything
    case CLOSE_MODAL_PAGE: {
      const createCustomerClosed = action.groupID === mp.createCustomer;
      const registerCustomerFormOpenedOnDisplay =
        state.processingCustomerState === 'register';
      if (createCustomerClosed && registerCustomerFormOpenedOnDisplay) {
        return { ...state, needToCloseCustomerFormOnDisplay: true };
      }
      return state;
    }
    case RESET_CUSTOMER_FORM_STATE:
      return {
        ...state,
        needToCloseCustomerFormOnDisplay: false,
        processingCustomerState: null,
      };
    case SET_HAS_PERMISSION:
      return { ...state, hasPermission: action.payload };
    case SET_FETCHED_INITIAL_SCREEN_DETAILS:
      return { ...state, fetchedInitialScreenDetails: action.payload };
    case SET_SCREENS:
      return { ...state, screens: action.payload };
    case SET_SELECTED_SCREEN:
      return { ...state, selectedScreen: action.payload };
    case SET_NEEDS_START_NEW:
      return { ...state, needsStartNew: action.payload };
    case SET_PROCESSING_CUSTOMER:
      return { ...state, processingCustomerState: action.payload };
    case SET_NEED_TO_CLOSE_CUSTOMER_FORM_ON_DISPLAY:
      return { ...state, needToCloseCustomerFormOnDisplay: action.payload };
    default:
      return state;
  }
};

export const getCustomerDisplayState = state =>
  getPluginSelector<CustomerDisplayState>(pluginID)(n => n)(state);

export const getCustomerDisplayPluginConfiguration = createSelector(
  state => getPluginConfiguration<Configuration>(pluginID)(state),
  config => {
    return config || {};
  },
);

export const getDefaultRegisterCustomerFieldTranslations = createSelector(
  state => getCurrentPosLanguage(state),
  lang =>
    defaultTranslations[['en', 'et'].includes(`${lang}`) ? lang : 'en']
      .registerCustomerForm,
);

export const getFieldTranslations = createSelector(
  state => getCustomerDisplayPluginConfiguration(state),
  state => getDefaultRegisterCustomerFieldTranslations(state),
  (conf, defaultTranslations) => {
    if (!conf || !conf?.fieldTranslations) {
      return defaultTranslations;
    }
    return R.mergeDeepLeft(conf.fieldTranslations, defaultTranslations);
  },
);

export const getFieldsToShowConfig = state =>
  getCustomerDisplayPluginConfiguration(state)?.fields ?? [];

export const getFieldsToShow = createSelector(
  state => getFieldsToShowConfig(state),
  state => getFieldTranslations(state),
  (config, translations) => {
    const fieldsToShow = Object.values(defaultCustomerCreationFieldsDisplay)
      .filter(field => !!config[field.name])
      .map(field =>
        Object.assign(field, {
          label:
            translations.label[field.name] ??
            defaultCustomerCreationFieldsDisplay[field.name].label,
          placeholder:
            translations.placeholder[field.name] ??
            defaultCustomerCreationFieldsDisplay[field.name].placeholder,
          requiredMessage:
            translations.requiredMessage[field.name] ??
            defaultCustomerCreationFieldsDisplay[field.name].requiredMessage,
        }),
      );
    return fieldsToShow;
  },
);

export const getCustomerCreationMessage = createSelector(
  state => getFieldsToShow(state),
  fieldsToShow => {
    return {
      action: ACTIONS.CUSTOMER.REGISTER_CUSTOMER,
      fields: fieldsToShow,
    };
  },
);

export const getLocaleFieldsFromConfig = createSelector(
  getCustomerDisplayPluginConfiguration,
  (config: Configuration) => config?.config?.localizationConstants,
);

export const getDefaultConfigForCentrifugoMessage = createSelector(
  state => getCompany(state),
  state => getSetting('receiptLogoURL')(state),
  state => getSelectedWarehouse(state),
  state => getCurrencySymbol(state),
  state => getCustomerCreationMessage(state),
  state => getCurrentPosLanguage(state),
  (
    company,
    brandLogo,
    warehouse,
    currencySymbol,
    customerCreationMessage,
    languageCode,
  ) =>
    ({
      appName: 'Customer Display App',
      language: 'en',
      localizationConstants: {
        currencySymbol,
        brandText: company.name,
        brandLogo,
        brandLogoType: 'JPG',
        locationText: (warehouse ?? {}).name,
        promotionsDefaultImage:
          'https://cdn.erply.com/images/469357/March-2021-newsletter.jpg',
        ...defaultTranslations.en,
        ...(defaultTranslations[languageCode] ?? {}),
      },
      theme: {
        mode: 'light',
        color: {
          background: '#FFFFFF',
          primary: '#32B17E',
          accent: '#009AD4',
          line: '#CCCCCC',
          headerBackground: '#EEEFF1',
          headerPrimaryFont: '#000000',
          headerSecondaryFont: '#585757',
          headerLine: '#CCCCCC',
          promotionsBackground: '#FFFFFF',
          promotionsFont: '#969696',
          promotionsLine: '#CCCCCC',
          productsBackground: '#FFFFFF',
          productsPrimaryFont: '#969696',
          productsSecondaryFont: '#585757',
          productsLine: '#EEEFF1',
          productsSummaryBackground: '#FFFFFF',
          productsSummaryPrimaryFont: '#969696',
          productsSummarySecondaryFont: '#585757',
          productsSummaryLine: '#EEEFF1',
          donationBackground: '#EEEFF1',
          donationPrimaryFont: '#000000',
          donationSecondaryFont: '#969696',
          donationInputBackground: '#FFFFFF',
          donationInputFont: '#000000',
          donationInputHintFont: '#969696',
          donationButton: '#32B17E',
          donationButtonFont: '#FFFFFF',
          donationSecondaryButton: '#FFFFFF',
          donationSecondaryButtonFont: '#000000',
          donationKeyboardBackground: '#FFFFFF',
          donationKeyBackground: '#EFF1F3',
          donationKeyFont: '#000000',
          donationThankingBackground: '#EEEFF1',
          donationThankingFont: '#000000',
          donationThankingLine: '#CCCCCC',
          tipBackground: '#EEEFF1',
          tipPrimaryFont: '#000000',
          tipSecondaryFont: '#969696',
          tipInputBackground: '#FFFFFF',
          tipInputFont: '#000000',
          tipInputHintFont: '#969696',
          tipButton: '#32B17E',
          tipButtonFont: '#FFFFFF',
          tipSecondaryButton: '#FFFFFF',
          tipSecondaryButtonFont: '#000000',
          tipKeyboardBackground: '#FFFFFF',
          tipKeyBackground: '#EFF1F3',
          tipKeyFont: '#000000',
          tipThankingBackground: '#EEEFF1',
          tipThankingFont: '#000000',
          tipThankingLine: '#CCCCCC',
          tipDeclinedBackground: '#000000',
          tipDeclinedFont: '#FFFFFF',
          tipDeclinedLine: '#1A1A1A',
          idleScreenBackground: '#FFFFFF',
          idleScreenFont: '#000000',
          idleScreenLine: '#CCCCCC',
          emailLookupBackground: '#EEEFF1',
          emailLookupPrimaryFont: '#000000',
          emailLookupSecondaryFont: '#969696',
          emailLookupInputBackground: '#FFFFFF',
          emailLookupInputFont: '#000000',
          emailLookupInputHintFont: '#969696',
          emailLookupButton: '#32B17E',
          emailLookupButtonFont: '#FFFFFF',
          phoneLookupBackground: '#EEEFF1',
          phoneLookupPrimaryFont: '#000000',
          phoneLookupSecondaryFont: '#969696',
          phoneLookupInputBackground: '#FFFFFF',
          phoneLookupInputFont: '#000000',
          phoneLookupInputHintFont: '#969696',
          phoneLookupButton: '#32B17E',
          phoneLookupButtonFont: '#FFFFFF',
          registerCustomerBackground: '#FFFFFF',
          registerCustomerPrimaryFont: '#000000',
          registerCustomerSecondaryFont: '#969696',
          registerCustomerButton: '#32B17E',
          registerCustomerButtonFont: '#FFFFFF',
          registerCustomerSecondaryButton: '#FFFFFF',
          registerCustomerSecondaryButtonFont: '#000000',
          registerCustomerInputLabelFont: '#000000',
          registerCustomerInputBackground: '#FFFFFF',
          registerCustomerInputFont: '#000000',
          registerCustomerInputHintFont: '#969696',
          registerCustomerInputErrorFont: '#FF6262',
          registerCustomerFormBackground: '#EEEFF1',
          registerCustomerFormPrimaryFont: '#000000',
          registerCustomerFormLine: '#CCCCCC',
          registerCustomerSuccessBackground: '#EEEFF1',
          registerCustomerSuccessFont: '#000000',
          registerCustomerSuccessLine: '#CCCCCC',
          registerCustomerFailedBackground: '#EEEFF1',
          registerCustomerFailedFont: '#000000',
          registerCustomerFailedLine: '#CCCCCC',
          couponBackground: '#EFF1F3',
          couponPrimaryFont: '#000000',
          couponSecondaryFont: '#969696',
          couponInputBackground: '#FFFFFF',
          couponInputFont: '#000000',
          couponInputHintFont: '#969696',
          couponButton: '#32B17E',
          couponButtonFont: '#FFFFFF',
          couponSecondaryButton: '#FFFFFF',
          couponSecondaryButtonFont: '#000000',
          couponSuccessBackground: '#FFFFFF',
          couponSuccessFont: '#000000',
          couponSuccessLine: '#EFF1F3',
          couponFailedBackground: '#FFFFFF',
          couponFailedFont: '#000000',
          couponFailedLine: '#EFF1F3',
          upsellBackground: '#FFFFFF',
          upsellPrimaryFont: '#000000',
          upsellSecondaryFont: '#969696',
          upsellButton: '#32B17E',
          upsellButtonFont: '#FFFFFF',
          upsellSecondaryButton: '#FFFFFF',
          upsellSecondaryButtonFont: '#000000',
          upsellFormBackground: '#EFF1F3',
          upsellFormLine: '#CCCCCC',
          upsellProductPrimaryFont: '#000000',
          upsellProductSecondaryFont: '#969696',
          upsellProductBackground: '#FFFFFF',
          upsellProductLine: '#32B17E',
          upsellSuccessBackground: '#EFF1F3',
          upsellSuccessFont: '#000000',
          upsellSuccessLine: '#EFF1F3',
          upsellFailedBackground: '#EFF1F3',
          upsellFailedFont: '#000000',
          upsellFailedLine: '#CCCCCC',
          thankYouScreenBackground: '#FFFFFF',
          thankYouScreenFont: '#000000',
          thankYouScreenLine: '#CCCCCC',
          qrCodeBackground: '#FFFFFF',
          qrCodeForeground: '#000000',
          qrPrimaryFont: '#000000',
          qrSecondaryFont: '#969696',
          rateServiceBackground: '#FFFFFF',
          rateServiceHeaderBackground: '#FBFBFB',
          rateServiceLine: '#EEEFF1',
          rateServiceStarUnselected: '#CCCCCC',
          rateServiceStarSelected: '#F2C94C',
          rateServicePrimaryFont: '#000000',
          rateServiceSecondaryFont: '#969696',
          rateServiceInputFont: '#000000',
          rateServiceInputHintFont: '#585757',
          rateServicePrimaryButton: '#009AD4',
          rateServicePrimaryButtonFont: '#FFFFFF',
          rateServiceSecondaryButton: '#FFFFFF',
          rateServiceSecondaryButtonFont: '#585757',
          customerCheckInHeaderBackground: '#FBFBFB',
          customerCheckInBackground: '#FFFFFF',
          customerCheckInLine: '#EEEFF1',
          customerCheckInPrimaryFont: '#000000',
          customerCheckInSecondaryFont: '#969696',
          customerCheckInAddCustomerBackground: '#FF6262',
          customerCheckInAddCustomerFont: '#FFFFFF',
          customerCheckInLookUpCustomerBackground: '#009AD4',
          customerCheckInLookUpCustomerFont: '#FFFFFF',
          customerCheckInCouponBackground: '#0E9B62',
          customerCheckInCouponFont: '#FFFFFF',
        },
      },
      settings: {
        purchaseInactivityTimeout: 60000,
        showDefaultContent: 'PROMOTIONS',
        showWideKeyboard: false,
        defaultPhoneSmallKeyboardState: 'NUMBERS',
        dateTimeConfig: {
          dateFormat: 'EU',
          timeFormat: '24H',
        },
        checkInConfig: {
          addCustomerMessage: customerCreationMessage,
          lookUpCustomerMessage: {
            action: 'GET_CUSTOMER_PHONE',
          },
          couponMessage: {
            action: 'ENTER_COUPON_CODE',
            type: 'ALPHANUMERIC',
          },
        },
      },
      fontConfig: {
        productsSummaryPrimary: {
          fontSize: 14,
          fontWeight: 'REGULAR',
        },
        productsSummarySecondary: {
          fontSize: 18,
          fontWeight: 'BOLD',
        },
      },
    } as Configuration['config']),
);

export const getShouldShowOpenCustomerDisplayButton = createSelector(
  state => getCustomerDisplayPluginConfiguration(state),
  displayConfig => {
    return displayConfig?.shouldShowOpenCustomerDisplayButton ?? true;
  },
);

export const getChannelPasscode = state =>
  getCustomerDisplayPluginConfiguration(state)?.passCode2;

export const getConfigForDisplay = state => {
  const defaultConfig = getDefaultConfigForCentrifugoMessage(state);
  const existingConf =
    getCustomerDisplayPluginConfiguration(state)?.config ?? {};
  return R.mergeDeepLeft(existingConf, defaultConfig);
};

export const getSetConfigCentrifugoMessage = createSelector(
  state => getConfigForDisplay(state),
  config => {
    const msg = {
      action: 'SET_CONFIG',
      config,
    };
    return msg;
  },
);
export const getLocalizationConstantsFromDisplayConfig = state => {
  return getConfigForDisplay(state).localizationConstants;
};

export const getSettingsFromDisplayConfig = state => {
  return getConfigForDisplay(state).settings;
};

export const getPromotionsArray = createSelector(
  state => getCustomerDisplayPluginConfiguration(state),
  config => {
    return config?.promotions || [];
  },
);

export const getSmallPromotionsArray = createSelector(
  state => getCustomerDisplayPluginConfiguration(state),
  config => {
    return config?.smallPromotions || [];
  },
);

export const getSetPromotionsMessage = createSelector(
  state => getPromotionsArray(state),
  promotions => {
    return {
      action: 'SET_PROMOTIONS',
      promotions,
    };
  },
);

export const getSetSmallPromotionsMessage = createSelector(
  state => getSmallPromotionsArray(state),
  promotions => {
    return {
      action: 'SET_SMALL_PROMOTIONS',
      promotions,
    };
  },
);

export const getDonationProductId = state => {
  return getCustomerDisplayPluginConfiguration(state)?.donationProductId || 0;
};

export const getTipProductId = state => {
  return getCustomerDisplayPluginConfiguration(state)?.tipProductId ?? 0;
};

export const getDiscountAsPercentage = state => {
  return (
    getCustomerDisplayPluginConfiguration(state)?.rowDiscountAsPercentage ??
    false
  );
};

export const getAfterSaleAction = state => {
  return (
    getCustomerDisplayPluginConfiguration(state)?.afterSaleAction ?? 'nothing'
  );
};

export const getOpenCustomerFormOnCreate = state => {
  return (
    getCustomerDisplayPluginConfiguration(state)?.openCustomerFormOnCreate ??
    false
  );
};

export const getQRCodeConfig = (state): QrCodeConfig => {
  return (
    getCustomerDisplayPluginConfiguration(state)?.qrConfig ||
    defaultQrCodeConfig
  );
};

export const getCustomQRCodeMessage = createSelector(
  state => getQRCodeConfig(state),
  qrConf => {
    const { qrCodeData = '', qrTitle = '', qrSubtitle = '' } = qrConf;
    return {
      action: 'SHOW_QR',
      qrCodeData,
      qrTitle,
      qrSubtitle,
    };
  },
);

export const getChannelPassphrase = state => {
  const passCode = getChannelPasscode(state);
  const code: string[] = Array(Math.ceil(passCode.length / 4))
    .fill(0)
    .map((z, i) => passCode.slice(i * 4, (i + 1) * 4));
  return code.map(c => UniquePrefixWordlist.decode(c));
};

export const getChannelName = state => {
  const clientCode = getClientCode(state);
  const posID = getSelectedPos(state)?.pointOfSaleID;
  const passCode = getChannelPasscode(state);
  return passCode ? `${clientCode}${posID}${passCode}` : null;
};

export const getDynamicTipProduct = createSelector(
  state => getTipProductId(state),
  state => getProductsInShoppingCart(state),
  (
    tipProductId,
    shoppingCartProducts,
  ): null | (CartItem & { extraParams: { displayTipPercentage: number } }) => {
    if (!tipProductId) {
      return null;
    }
    // Find if cart has a tip product, added from display by using `percentage` option
    const tipProduct = shoppingCartProducts.find(
      p =>
        Number(p.productID) === Number(tipProductId) &&
        p.extraParams?.displayTipPercentage,
    );

    if (!tipProduct) {
      return null;
    }
    return tipProduct;
  },
);
export const getChannel = state => {
  const channelName = getChannelName(state);
  if (!channelName) return null;
  return `customer_display:${channelName}`;
};

export const getNeedsStartNew = state =>
  getCustomerDisplayState(state).needsStartNew;

export const getProcessingCustomer = state =>
  getCustomerDisplayState(state).processingCustomerState;

export const getNeedToCloseCustomerFormOnDisplay = state =>
  getCustomerDisplayState(state).needToCloseCustomerFormOnDisplay;

export const getTipProductPrice = createSelector(
  state => getDynamicTipProduct(state),
  state => getProductsInShoppingCart(state),
  (tipProduct, shoppingCartProducts) => {
    if (!tipProduct) {
      return '';
    }
    return (
      (shoppingCartProducts
        .filter(p => p.orderIndex !== tipProduct.orderIndex)
        .reduce((total, nextRow) => total + Number(nextRow.rowTotal), 0) *
        tipProduct.extraParams.displayTipPercentage) /
      100
    ).toFixed(2);
  },
);

export const getCustomerDisplayData = createSelector(
  state => getShowPricesWithTax(state),
  state => getProductsInShoppingCart(state),
  state => getVisibleTotalDiscount(state),
  state => getTotalTax(state),
  state => getTotalNet(state),
  state => getTotal(state),
  state => getCurrencyFormatter(state),
  state => getShoppingCartDiscountsDict(state),
  (
    isWithTax,
    shoppingCart,
    totalDiscount,
    totalVat,
    totalNet,
    total,
    formatCurrency,
    shoppingCartDiscounts,
  ) => {
    return {
      products: shoppingCart.map(o => ({
        id: o.productID,
        name: o.name,
        code: o.code,
        qty: o.amount,
        disc: (shoppingCartDiscounts[o.rowNumber] || [])
          ?.map(a => `${a.affected}x -${a.percentage}%`)
          .join('\n'),
        price: isWithTax
          ? formatCurrency(Number(o.priceListPriceWithVAT))
          : formatCurrency(Number(o.priceListPrice)),
      })),
      summary: {
        disc: formatCurrency(totalDiscount, { roundDown: true }),
        tax: formatCurrency(totalVat),
        net: formatCurrency(totalNet),
        total: formatCurrency(total),
      },
    };
  },
);

export default customerDisplayReducer;
